
// Packages
import { defineComponent, PropType } from 'vue';

// Types
import type {
  CartItem,
  CancellationProtection,
} from '@white-label-types/parking-checkout';
import type {
  CancellationPolicy,
  AmendmentPolicy,
  StandardPolicy,
} from '@white-label-types/parking-booking';

// Helpers
import { formatPrice } from '@white-label-helper/helper-payment';
// Stores
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
// Packages
import { mapState } from 'pinia';

import RadioButton from '../radio-button/radio-button.vue';
import StandardPolicies from '../cancellation-and-amendment-policy/sub-components/standard-policies.vue';

export default defineComponent({
  name: 'CancellationAndAmendmentPolicy',

  components: {
    RadioButton,
    IconS3: () => import('@white-label-icon/icons-s3'),
    IconShield: () => import('@white-label-icon/icon-shield'),
    IconCheck: () => import('@white-label-icon/icon-check'),
    StandardPolicies,
  },

  props: {
    cancellationPolicies: {
      type: Array as PropType<
        | CancellationPolicy[]
        | CartItem['inventory_option']['cancellation_policies'][]
      >,
      default: () => [],
    },
    amendmentPolicies: {
      type: Array as PropType<
        AmendmentPolicy[] | CartItem['inventory_option']['amendment_policies'][]
      >,
      default: () => [],
    },
    isProtected: {
      type: Boolean,
      required: true,
    },
    cancellationProtection: {
      type: Array as PropType<CancellationProtection[]>,
      required: true,
    },
    isBookingFeeCharged: {
      type: Boolean,
      required: true,
      default: false,
    },
    bundleName: {
      type: String,
      default: '',
    },
    isBundleInfoModal: {
      type: Boolean,
      default: false,
    },
    cancellationProtectionAutoAddProp: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      standardPolicyExpanded: false,
      upgradedProtectionExpanded: true,
      selectedOption: 'standardOption',
    };
  },

  computed: {
    ...mapState(useCartCheckoutStore, {
      readCartBundle: 'readCartBundle',
      readCartItems: 'readCartItems',
    }),
    upgradedProtectionLabel() {
      return `
        Yes, add Upgraded policy for
        ${formatPrice(this.cancellationProtectionTotal)}
      `;
    },
    standardPolicyDetailsAriaLabel() {
      return this.standardPolicyExpanded
        ? this.$t(
            'cancellationAndAmendment.hideStandardPolicyDetails',
          ).toString()
        : this.$t(
            'cancellationAndAmendment.showStandardPolicyDetails',
          ).toString();
    },
    upgradedProtectionDetailsAriaLabel() {
      return this.upgradedProtectionExpanded
        ? this.$t(
            'cancellationAndAmendment.hideUpgradedPolicyDetails',
          ).toString()
        : this.$t(
            'cancellationAndAmendment.showUpgradedPolicyDetails',
          ).toString();
    },
    isAdditionalCancellationProtectionAvailable(): boolean {
      // Hide the option to add protection when viewing bundle info modal
      if (this.isBundleInfoModal) {
        return false;
      }

      // As a temporary solution, we are removing the ability to add cancellation protection
      // when a bundle item is added to the cart as it is causing some issues.
      // More details - https://magusa.atlassian.net/browse/ECOM-3973
      if (this.cancellationProtection.some((item) => item.is_bundle_product)) {
        return false;
      }
      return this.isBundleCancellationProtectionAutoAdded
        ? this.nonBundleProductsUpgradePolicies.length > 0
        : this.cancellationProtection.some((item) => item.is_available);
    },
    cancellationProtectionTotal(): number {
      return this.cancellationProtection.reduce(
        (acc, item) => acc + (item.totals?.total ?? 0),
        0,
      );
    },
    /**
     * Returns the standard cancellation and amendment policies of all products in cart.
     * @returns an array of StandardPolicy objects.
     */
    allProductsStandardPolicies(): StandardPolicy[] {
      return this.cancellationPolicies.map(
        (cancellationPolicyItem: CancellationPolicy) => {
          const amendmentPolicyItem = this.amendmentPolicies.find(
            (item: AmendmentPolicy) =>
              item.product_id === cancellationPolicyItem.product_id,
          );
          return {
            ...cancellationPolicyItem,
            amendment_policy: amendmentPolicyItem
              ? amendmentPolicyItem.amendment_policy
              : '',
          } as StandardPolicy;
        },
      );
    },
    /**
     * Returns the standard cancellation and amendment policies of all cart items that are part of a bundle.
     * If the bundle has cancellation protection auto-added, the standard cancellation policy text of each eligible product is updated
     * with the upgraded policy text, and 'is_cancellation_protection_auto_added' is set to true.
     * @returns an array of StandardPolicy objects.
     */
    bundleProductsStandardPolicies(): StandardPolicy[] {
      let bundleProductsStandardPolicies =
        this.allProductsStandardPolicies.filter(
          (item: StandardPolicy) => item.is_bundle_product,
        );

      if (this.isBundleCancellationProtectionAutoAdded) {
        bundleProductsStandardPolicies = bundleProductsStandardPolicies.map(
          (item) => {
            const cancellationProtectionItem = this.cancellationProtection.find(
              (protectionItem) => protectionItem.product_id === item.product_id,
            );
            if (
              cancellationProtectionItem &&
              cancellationProtectionItem.is_available
            ) {
              return {
                ...item,
                cancellation_policy:
                  cancellationProtectionItem.cancellation_policies[0].message,
                is_cancellation_protection_auto_added: true,
              };
            }
            return item;
          },
        );
      }

      return bundleProductsStandardPolicies;
    },
    /**
     * Returns the standard cancellation and amendment policies of all cart items that are not part of a bundle.
     * @returns an array of StandardPolicy objects.
     */
    nonBundleProductsStandardPolicies(): StandardPolicy[] {
      return this.allProductsStandardPolicies.filter(
        (item: StandardPolicy) => !item.is_bundle_product,
      );
    },
    /**
     * Returns the cancellation protection policy details of cart items that
     * do have upgraded cancellation protection available, and are part of a bundle.
     * If the bundle has cancellation protection auto-added, the upgraded policy details of the affected bundle items
     * will be shown in the standard policy section, so we don't want to show them in the upgraded section, and we return an empty array here.
     * @returns an array of CancellationProtection objects or an empty array.
     */
    bundleProductsUpgradePolicies(): CancellationProtection[] | [] {
      if (this.isBundleCancellationProtectionAutoAdded) {
        return [];
      }
      return this.cancellationProtection.filter(
        (item: CancellationProtection) =>
          item.is_bundle_product && item.is_available,
      );
    },
    /**
     * Returns the cancellation protection policy details of cart items that
     * do have upgraded cancellation protection available, and are not part of a bundle.
     * @returns an array of CancellationProtection objects or an empty array.
     */
    nonBundleProductsUpgradePolicies(): CancellationProtection[] | [] {
      return this.cancellationProtection.filter(
        (item: CancellationProtection) =>
          !item.is_bundle_product && item.is_available,
      );
    },
    /**
     * Returns a boolean indicating whether a bundle in the cart has cancellation protection auto-added. Returns false if no bundle in cart.
     * @returns a boolean.
     */
    isBundleCancellationProtectionAutoAdded(): boolean {
      return Boolean(
        this.readCartBundle?.is_cancellation_protection_auto_add ||
        this.cancellationProtectionAutoAddProp,
      );
    },
    /**
     * Returns a boolean indicating whether there are any non-bundle cart items that have cancellation protection added.
     * @returns a boolean.
     */
    areNonBundleProductsProtected(): boolean {
      // Return false when viewing bundle info modal and we show only bundle products
      if (this.isBundleInfoModal) {
        return false;
      }
      return (
        this.readCartItems.some(
          (item) => !item?.bundle_id && item?.is_protected,
        ) || false
      );
    },
  },

  mounted() {
    const setUpgraded = this.isBundleCancellationProtectionAutoAdded
      ? this.areNonBundleProductsProtected
      : this.isProtected;
    if (setUpgraded) {
      this.selectedOption = 'upgradedOption';
    }
  },

  methods: {
    onRadioSelect(value: string): void {
      this.selectedOption = value;

      if (value === 'standardOption') {
        this.$emit('remove-cancellation-protection');
      } else {
        this.$emit('add-cancellation-protection');
      }
    },
    standardPolicySectionClick(event) {
      const standardPolicyRadio = this.$refs.standardPolicyRadio?.$el;
      if (standardPolicyRadio && standardPolicyRadio.contains(event.target))
        return;
      this.standardPolicyExpanded = !this.standardPolicyExpanded;
    },
    upgradedPolicySectionClick(event) {
      const upgradedPolicyRadio = this.$refs.upgradedPolicyRadio?.$el;
      if (upgradedPolicyRadio && upgradedPolicyRadio.contains(event.target))
        return;
      this.upgradedProtectionExpanded = !this.upgradedProtectionExpanded;
    },
  },
});
